import type { LineSeriesOption } from "echarts/charts";
import type { GridComponentOption, TitleComponentOption, TooltipComponentOption } from "echarts/components";
import type { ComposeOption } from "echarts/core";
import type { Locale } from "verdi-common/i18n";
import { AxisSide, type AxisSpec, type TimeAxisPanelSpec } from "../ChartSpec";
import { formatAxisPointerTime, formatTooltipValue } from "../format";
import type { GetDisplayInfo } from "../units/use-get-display-info";
import { type AxisScale, formatTickLabel } from "./layout/axis-layout";
import {
    AXIS_LABEL_COLOR,
    GRID_BORDER_COLOR,
    HOVER_LABEL_BORDER_PX,
    HOVER_LABEL_FONT_SIZE_PX,
    HOVER_LABEL_PADDING_X_PX,
    HOVER_LABEL_PADDING_Y_PX,
    HOVER_LABEL_RADIUS_PX,
    X_AXIS_POINTER_LABEL_MARGIN_PX,
    X_AXIS_TICK_LABEL_FONT_SIZE_PX,
    X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
    X_AXIS_TICK_LABEL_MARGIN_PX,
    Y_AXIS_BASE_OFFSET_PX,
    Y_TICK_FONT_SIZE_PX,
} from "./layout/plot-geometry";
import type { PlotLayout } from "./layout/plot-layout";
import type { PanelChrome } from "./plan-panel-chrome";
import type { PreparedSeries } from "./prepare-series";
import { renderTooltipHtml } from "./tooltip/tooltip-html";

const CROSSHAIR_LABEL_BACKGROUND = "#4A4D46";

type ECOption = ComposeOption<LineSeriesOption | TitleComponentOption | TooltipComponentOption | GridComponentOption>;

/**
 * Y axes are driven entirely by pre-computed scales (see layout/axis-layout.ts): we set numeric
 * min/max/interval so ECharts renders exactly the ticks we already measured for gutter width.
 * Ticks above `maxVisibleTick` sit in the unit-badge headroom band and render as empty strings —
 * a pure closure over an already-computed number, not state mutated by an ECharts callback.
 */
const buildYAxes = (
    axes: AxisSpec[],
    axisScales: Map<string, AxisScale>,
    activeLeftAxisId: string | undefined,
): ECOption["yAxis"] => {
    // All axes share one offset, so only one per side can show tick labels without overlapping.
    // Extra axes stay in the list (series still bind to them) but hide labels. Keep in sync with
    // visibleAxesForSide in plot-layout.ts.
    const firstRightAxisId = axes.find((axis) => axis.side === AxisSide.Right)?.id;

    return axes.map((axis) => {
        const isVisible = axis.side === AxisSide.Left ? axis.id === activeLeftAxisId : axis.id === firstRightAxisId;
        const scale = axisScales.get(axis.id);

        return {
            id: axis.id,
            type: "value",
            position: axis.side,
            offset: Y_AXIS_BASE_OFFSET_PX,
            // No scale entry means this axis has no data. Omit min/max/interval and let ECharts
            // auto-range: nothing is plotted, so exact ticks don't matter.
            ...(scale ? { min: scale.niceMin, max: scale.niceMax, interval: scale.step } : {}),
            axisLabel: {
                show: isVisible,
                color: AXIS_LABEL_COLOR,
                // fontWeight/fontSize must stay in sync with Y_TICK_FONT in ./layout/plot-geometry.ts —
                // the gutter width was measured with that font string.
                fontWeight: "lighter",
                fontSize: Y_TICK_FONT_SIZE_PX,
                hideOverlap: true,
                margin: 0,
                formatter: (value: number) => {
                    if (!scale) return formatTooltipValue(value);
                    return value <= scale.maxVisibleTick ? formatTickLabel(value, scale.step) : "";
                },
            },
            splitLine: {
                show: false,
            },
        };
    });
};

export const buildTimePanelOptions = (
    spec: TimeAxisPanelSpec,
    series: PreparedSeries[],
    layout: PlotLayout,
    axisScales: Map<string, AxisScale>,
    window: { startMs: number; endMs: number },
    activeLeftAxisId: string | undefined,
    getDisplayInfo: GetDisplayInfo,
    locale: Locale,
    chrome: PanelChrome,
): ECOption => {
    // The badge names a device every series shares; when it can't, each tooltip row names its own.
    const showDeviceName = chrome.deviceName === null;
    // Sensor names are never on the badge, so the tooltip shows them on the same terms as the legend.
    const showSensorClusterName = chrome.legend.sensorClusters;
    const showSensorElementName = chrome.legend.sensorElements;
    const seriesByStreamId = new Map(series.map((s) => [s.streamId, s]));

    return {
        // Grant corrections (a peer panel widening the shared gutter) and axis-toggle reflows must
        // land as an instant snap, not a tween — an animated grid change is what turns an already-
        // brief cross-panel realignment into a visible slide. See use-panel-plot-layout.ts.
        animation: false,
        grid: {
            // Border handled by PlotChromeLayer, a DOM overlay in panel.tsx.
            show: false,
            // We set exact pixel gutters from the shared width-group grant; containLabel would
            // re-fit the grid around whatever labels ECharts measures and fight those numbers.
            containLabel: false,
            top: 0,
            left: layout.gutters.left,
            right: layout.gutters.right,
            bottom: layout.xAxisBandPx,
        },
        tooltip: {
            // showContent, not show: show:false would take the crosshair with it.
            showContent: chrome.tooltip,
            trigger: "axis",
            textStyle: { lineHeight: 14, fontSize: 13 },
            padding: 8,
            formatter: (params) =>
                Array.isArray(params)
                    ? renderTooltipHtml(params, {
                          seriesByStreamId,
                          getDisplayInfo,
                          showDeviceName,
                          showSensorClusterName,
                          showSensorElementName,
                          locale,
                      })
                    : "",

            // This is the x-axis vertical line and label that follows the cursor
            axisPointer: {
                type: "line",
                label: {
                    show: true,
                    color: "#FDFDFD",
                    backgroundColor: CROSSHAIR_LABEL_BACKGROUND,
                    // Border matches the fill: the tags carry one, and the two boxes have to measure
                    // the same. Everything else comes from the shared hover label tokens.
                    borderColor: CROSSHAIR_LABEL_BACKGROUND,
                    borderWidth: HOVER_LABEL_BORDER_PX,
                    padding: [HOVER_LABEL_PADDING_Y_PX, HOVER_LABEL_PADDING_X_PX + 2],
                    fontSize: HOVER_LABEL_FONT_SIZE_PX,
                    fontWeight: 400,
                    borderRadius: HOVER_LABEL_RADIUS_PX,
                    align: "center",
                },
            },
        },
        xAxis: {
            type: "time",
            // The x-axis should show the user's desired time range - not whatever the actual set of
            // downloaded data starts and ends at.
            min: window.startMs,
            max: window.endMs,
            tooltip: {
                show: false,
            },
            axisLine: {
                show: false,
            },
            axisPointer: {
                label: {
                    margin: X_AXIS_POINTER_LABEL_MARGIN_PX,
                    formatter: (params) => formatAxisPointerTime(params.value as number | string | Date),
                },
            },
            axisLabel: {
                color: AXIS_LABEL_COLOR,
                fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
                lineHeight: X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
                margin: X_AXIS_TICK_LABEL_MARGIN_PX,
                showMaxLabel: false,
                showMinLabel: false,
                hideOverlap: true,

                formatter: {
                    year: "{yyyy}",
                    month: "{MMMM}",
                    day: "{MMM} {d}",
                    hour: "{h}:{mm}{a}",
                    minute: "{h}:{mm}{a}",
                },
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                show: true,
                showMinLine: false,
                showMaxLine: false,
                lineStyle: {
                    color: GRID_BORDER_COLOR,
                },
            },
        },
        yAxis: buildYAxes(spec.axes, axisScales, activeLeftAxisId),
        series: series.map((s) => {
            const entry = spec.streams.find((stream) => stream.streamId === s.streamId);
            const axis = spec.axes.find((a) => a.id === entry?.axisId);

            return {
                id: String(s.streamId),
                yAxisId: axis?.id,
                data: s.points,
                type: "line",
                showSymbol: false,
                color: s.color,
                connectNulls: false,
                // HoverValues marks every series; echarts' emphasis marks only the nearest.
                emphasis: { disabled: true },
            };
        }),
    };
};
